// cikada-public-gateway fronts cikada-api for signed-out visitors: it allowlists
// the visitor-facing actions and returns the finished result inline, so the
// browser never needs a token or a run-polling round trip.
const API_BASE = "/api/clothes/cikada-public-api";

export type Prediction = {
  value?: string;
  confidence?: number;
  top_k?: Array<[string, number]>;
};

export type Appraisal = {
  price_eur?: { median?: number; p25?: number; p75?: number; comparable_count?: number };
  sell_probability?: number;
  days_to_sale?: { median?: number; p25?: number; p75?: number };
  comparable_count?: number;
  sample_size?: number;
};

export type SearchResult = {
  ok?: boolean;
  vector?: string;
  vector_id?: string;
  item?: {
    attributes?: Record<string, Prediction>;
    appraisal?: Appraisal;
  };
  comparables?: unknown[];
  matches?: unknown[];
};

type RunEnvelope = {
  status?: string;
  success?: boolean;
  output?: unknown;
  error?: unknown;
  errors?: unknown[];
};

function errorMessage(error: unknown): string {
  if (typeof error === "string" && error) return error;
  if (error && typeof error === "object") {
    const detail = error as { message?: unknown; code?: unknown };
    if (typeof detail.message === "string" && detail.message) return detail.message;
    if (typeof detail.code === "string" && detail.code) return detail.code;
  }
  return "Classification failed.";
}

function unwrap<T>(value: unknown): T {
  const candidate =
    value && typeof value === "object" && "output" in value
      ? (value as { output: unknown }).output
      : value;

  if (candidate && typeof candidate === "object" && "ok" in candidate) {
    const result = candidate as { ok?: boolean; error?: unknown };
    if (result.ok === false) throw new Error(errorMessage(result.error));
  }

  return (candidate || {}) as T;
}

async function invoke<T>(payload: Record<string, unknown>, signal?: AbortSignal) {
  const response = await fetch(`${API_BASE}/ops/receive`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ payload }),
    signal,
  });

  if (!response.ok) throw new Error(`Classification request failed (${response.status}).`);

  // The gateway waits for the classifier and answers inline, so there is no run
  // to poll — the run endpoint would need a token a signed-out visitor lacks.
  const run = (await response.json()) as RunEnvelope;
  if (run.success === false) {
    throw new Error(errorMessage(run.errors?.[0] ?? run.error));
  }
  return unwrap<T>(run.output ?? run);
}

/**
 * Takes JPEG base64 the caller has already produced. Decoding a large phone
 * photo a second time here used to fail intermittently on iOS, where Safari
 * drops image decodes under memory pressure — the preview would render and the
 * analysis would then fail on the very same file. The caller decodes once and
 * passes the result through instead.
 */
export async function searchGarment(
  images: string[],
  signal?: AbortSignal,
): Promise<SearchResult> {
  return invoke<SearchResult>({ action: "search", images }, signal);
}
